import { useState, useEffect, useMemo } from 'react';
import { customersApi, projectRequestsApi } from '../../../services/api';
import { Customer, PaginatedResponse, ProjectRequestListItem } from '../../../types';

export const useProjectRequests = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | ''>('');
  const [status, setStatus] = useState<string>('');
  const [data, setData] = useState<PaginatedResponse<ProjectRequestListItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const load = async (page?: number) => {
    try {
      setLoading(true);
      const res = await projectRequestsApi.list({
        customer_id: selectedCustomer === '' ? undefined : Number(selectedCustomer),
        status: status || undefined,
        page,
      });
      setData(res);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load project requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const cs = await customersApi.getAll({ status: 'active', per_page: 1000 });
      setCustomers(cs.data);
    })();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, status]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter project requests based on search query
  const filteredRequests = useMemo(() => {
    if (!data?.data) return [];
    if (!debouncedSearchQuery.trim()) return data.data;

    const query = debouncedSearchQuery.toLowerCase();
    return data.data.filter(
      (r) =>
        r.project_name.toLowerCase().includes(query) ||
        (r.customer_name && r.customer_name.toLowerCase().includes(query)) ||
        (r.submitter?.name && r.submitter.name.toLowerCase().includes(query)) ||
        (r.funding_type && r.funding_type.toLowerCase().includes(query)) ||
        (r.work_type && r.work_type.toLowerCase().includes(query)) ||
        (r.work_request && r.work_request.some(wr => wr.toLowerCase().includes(query))) ||
        (r.impacted_parcels !== null && r.impacted_parcels !== undefined && r.impacted_parcels.toString().includes(query)),
    );
  }, [data, debouncedSearchQuery]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = filteredRequests.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return {
    customers,
    selectedCustomer,
    setSelectedCustomer,
    status,
    setStatus,
    data,
    loading,
    error,
    setError,
    searchQuery,
    setSearchQuery,
    filteredRequests,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    startIndex,
    endIndex,
    currentRequests,
    handlePageChange,
    load,
  };
};
